import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { z } from "zod";
import {
  CreateConsultationSchema,
  CreatePatientSchema,
  ConsultationDTOSchema,
  PatientDTOSchema,
  ConsultationStatus,
  Language,
  UserRole,
  Gender,
} from "@arna-med/shared";
import { useSession } from "@/lib/auth-client";
import { useAuth } from "@/contexts/AuthContext";
import { getLocalizedName } from "@/lib/utils";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  X,
  Search,
  Plus,
  Stethoscope,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { useCreateConsultation, usePatientConsultations } from "@/hooks/api/useConsultations";
import { usePatientSearch, useCreatePatient, usePatient, useSharePatient } from "@/hooks/api/usePatients";
import { useSpecialities } from "@/hooks/api/useSpecialities";
import {
  useNewConsultationTemplates,
  usePrimaryVisitTemplate,
} from "@/hooks/api/useTemplates";

type NewConsultationForm = z.infer<typeof CreateConsultationSchema>;
type PatientDTO = z.infer<typeof PatientDTOSchema>;
type ConsultationDTO = z.infer<typeof ConsultationDTOSchema>;

const PatientCreateResponseSchema = z.object({
  success: z.boolean(),
  data: PatientDTOSchema,
});

const HISTORY_LIMIT = 5;

const EMPTY_PATIENT_FORM = {
  name: "",
  personalNumber: "",
  gender: Gender.Male as Gender,
  phone: "",
  email: "",
  dateOfBirth: "",
};

export default function NewConsultation() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPatientId = searchParams.get("patientId");
  const { data: session } = useSession();
  const { profile } = useAuth();
  const currentUser = session?.user as
    | { role?: string; specialityId?: string | null }
    | undefined;
  const isDoctor = currentUser?.role === UserRole.Doctor;
  const doctorSpecialityId = currentUser?.specialityId ?? null;

  const [form, setForm] = useState<NewConsultationForm>({
    patientName: "",
    templateId: "",
    language: Language.Russian,
  });
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientDTO | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [selectedSpecialityId, setSelectedSpecialityId] = useState(
    isDoctor && doctorSpecialityId ? doctorSpecialityId : "",
  );
  const [doctorViewSpecialityId, setDoctorViewSpecialityId] = useState<string | null>(
    doctorSpecialityId,
  );

  // The doctor picks nothing by default: a primary visit is the consultation,
  // and the studies that are still their own document (ultrasound, echo — they
  // are issued on MoH form 213/у) live behind this disclosure.
  const [showStudyPicker, setShowStudyPicker] = useState(false);

  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [addPatientForm, setAddPatientForm] = useState(EMPTY_PATIENT_FORM);
  const [addPatientError, setAddPatientError] = useState("");
  const [duplicateConflict, setDuplicateConflict] = useState<{
    id: string;
    name: string;
    personalNumber: string;
  } | null>(null);

  const [historyPage, setHistoryPage] = useState(1);

  useEffect(() => {
    setHistoryPage(1);
  }, [selectedPatient?.id]);

  const { data: preselectedPatient } = usePatient(preselectedPatientId);

  const { data: searchResults = [], isFetching: isSearching } = usePatientSearch(searchQuery);

  const { data: specialities = [] } = useSpecialities();

  const { data: templates = [] } = useNewConsultationTemplates({
    specialityId: isDoctor ? doctorViewSpecialityId : selectedSpecialityId,
    enabled: showStudyPicker && (isDoctor ? !!doctorViewSpecialityId : !!selectedSpecialityId),
  });

  const {
    data: primaryVisitTemplate,
    isLoading: primaryVisitLoading,
    isError: primaryVisitMissing,
  } = usePrimaryVisitTemplate();

  // Selecting the primary visit is the default state of the form, not something
  // the doctor does. Only opening the study picker clears it.
  useEffect(() => {
    if (!showStudyPicker && primaryVisitTemplate) {
      setForm((f) =>
        f.templateId === primaryVisitTemplate.id
          ? f
          : { ...f, templateId: primaryVisitTemplate.id },
      );
    }
  }, [showStudyPicker, primaryVisitTemplate]);

  const { data: historyResult, isLoading: historyLoading } = usePatientConsultations({
    patientId: selectedPatient?.id,
    page: historyPage,
    limit: HISTORY_LIMIT,
  });

  const patientHistory: ConsultationDTO[] = historyResult?.data ?? [];
  const historyTotal = historyResult?.total ?? 0;
  const historyTotalPages = Math.max(1, Math.ceil(historyTotal / HISTORY_LIMIT));

  const createMutation = useCreateConsultation();
  const createPatientMutation = useCreatePatient();
  const sharePatientMutation = useSharePatient();

  const handleSearch = () => {
    if (searchInput.trim().length < 1) return;
    setSearchQuery(searchInput.trim());
    setShowDropdown(true);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelectPatient = (p: PatientDTO) => {
    setSelectedPatient(p);
    setForm((f) => ({ ...f, patientName: p.name, patientId: p.id }));
    setSearchInput(p.name);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (preselectedPatient && !selectedPatient) {
      setSelectedPatient(preselectedPatient);
      setForm((f) => ({ ...f, patientName: preselectedPatient.name, patientId: preselectedPatient.id }));
      setSearchInput(preselectedPatient.name);
      setShowDropdown(false);
    }
  }, [preselectedPatient, selectedPatient]);

  const handleClearPatient = () => {
    setSelectedPatient(null);
    setSearchInput("");
    setSearchQuery("");
    setShowDropdown(false);
    setForm((f) => ({ ...f, patientName: "", patientId: undefined }));
  };

  const handleAddPatientSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name: addPatientForm.name,
      personalNumber: addPatientForm.personalNumber,
      gender: addPatientForm.gender,
      ...(addPatientForm.phone && { phone: addPatientForm.phone }),
      ...(addPatientForm.email && { email: addPatientForm.email }),
      ...(addPatientForm.dateOfBirth && {
        dateOfBirth: addPatientForm.dateOfBirth,
      }),
    };
    const parsed = CreatePatientSchema.safeParse(payload);
    if (!parsed.success) {
      setAddPatientError(parsed.error.errors[0].message);
      return;
    }
    createPatientMutation.mutate(parsed.data, {
      onSuccess: (json) => {
        const patient = PatientCreateResponseSchema.parse(json).data;
        handleSelectPatient(patient);
        setShowAddPatient(false);
        setAddPatientForm(EMPTY_PATIENT_FORM);
        setAddPatientError("");
        setDuplicateConflict(null);
      },
      onError: (err) => {
        if (
          isAxiosError(err) &&
          err.response?.status === 409 &&
          err.response.data?.errorCode === "PATIENT_ALREADY_EXISTS"
        ) {
          setDuplicateConflict(err.response.data.existingPatient);
          setAddPatientError("");
        } else {
          setAddPatientError(t("common.error") || "Failed to create patient");
        }
      },
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = CreateConsultationSchema.safeParse(form);
    if (!parsed.success) {
      setError(parsed.error.errors[0].message);
      return;
    }
    setShowConsentDialog(true);
  };

  const handleConsentConfirm = () => {
    const parsed = CreateConsultationSchema.safeParse(form);
    if (!parsed.success) return;
    createMutation.mutate(parsed.data, {
      onSuccess: (json) => navigate(`/consultation/${json.data.id}`),
      onError: () => setError("Failed to create consultation"),
    });
  };

  const canStart =
    !!selectedPatient && !!form.templateId && !(primaryVisitLoading && !showStudyPicker);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const statusLabel = (c: ConsultationDTO) =>
    c.status === ConsultationStatus.Completed
      ? t("common.completed")
      : t("common.active");

  return (
    <Layout>
      <div className="w-full">
        <div
          className={`items-start gap-6 ${selectedPatient ? "grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" : "w-full max-w-lg mx-auto"}`}
        >
          <Card>
            <CardHeader>
              <CardTitle>{t("consultation.newTitle")}</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                id="new-consultation-form"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label>{t("consultation.patientName")}</Label>
                  <div className="relative" ref={dropdownRef}>
                    <div className="flex gap-1">
                      <Input
                        value={searchInput}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSearchInput(val);
                          if (selectedPatient) handleClearPatient();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearch();
                          }
                        }}
                        placeholder={t("consultation.patientSearchPlaceholder")}
                        disabled={!!selectedPatient}
                      />
                      {selectedPatient ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={handleClearPatient}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={handleSearch}
                          disabled={
                            isSearching || searchInput.trim().length < 1
                          }
                        >
                          <Search className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    {selectedPatient && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {selectedPatient.personalNumber}
                      </p>
                    )}

                    {!selectedPatient && showDropdown && (
                      <div className="absolute z-10 mt-1 w-full rounded-md border bg-background shadow-md">
                        {searchResults.length > 0 &&
                          searchResults.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              className="w-full flex items-center px-3 py-2 text-sm text-left cursor-pointer hover:bg-accent transition-colors"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelectPatient(p);
                              }}
                            >
                              <span className="font-medium">{p.name}</span>
                              <span className="text-muted-foreground ml-2">
                                {p.personalNumber}
                              </span>
                            </button>
                          ))}

                        {searchQuery.length > 0 &&
                          searchResults.length === 0 &&
                          !isSearching && (
                            <p className="px-3 py-2 text-sm text-muted-foreground">
                              {t("common.noResults")}
                            </p>
                          )}

                        <div className="border-t">
                          <button
                            type="button"
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-accent"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setShowDropdown(false);
                              setShowAddPatient(true);
                            }}
                          >
                            <Plus className="h-4 w-4" />
                            {t("consultation.addNewPatient")}
                          </button>
                        </div>
                      </div>
                    )}

                    {!selectedPatient && !showDropdown && (
                      <button
                        type="button"
                        className="mt-1 flex items-center gap-1 text-xs text-primary hover:underline"
                        onClick={() => setShowAddPatient(true)}
                      >
                        <Plus className="h-3 w-3" />
                        {t("consultation.addNewPatient")}
                      </button>
                    )}
                  </div>
                </div>

                {/* The primary visit. Shown as what will happen, not as an
                    option to weigh: the doctor cannot know the diagnosis before
                    the conversation, so there is nothing here to choose. */}
                <div className="space-y-2">
                  <Label>{t("consultation.visitType")}</Label>
                  <div
                    className={`rounded-md border p-3 ${
                      showStudyPicker ? "border-input" : "border-primary bg-primary/5"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Stethoscope className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {t("consultation.primaryVisit")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {t("consultation.primaryVisitHint")}
                        </p>
                      </div>
                    </div>
                    {primaryVisitMissing && !showStudyPicker && (
                      <p className="mt-2 text-xs text-destructive">
                        {t("consultation.primaryVisitUnavailable")}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                    onClick={() => {
                      const next = !showStudyPicker;
                      setShowStudyPicker(next);
                      if (next) setForm((f) => ({ ...f, templateId: "" }));
                    }}
                  >
                    <ChevronDown
                      className={`h-3 w-3 transition-transform ${showStudyPicker ? "rotate-180" : ""}`}
                    />
                    {showStudyPicker
                      ? t("consultation.backToPrimaryVisit")
                      : t("consultation.chooseStudyInstead")}
                  </button>
                </div>

                {showStudyPicker && (
                <div className="space-y-2">
                  <Label>{t("consultation.speciality")}</Label>
                  <select
                    value={isDoctor ? (doctorViewSpecialityId ?? "") : selectedSpecialityId}
                    onChange={(e) => {
                      if (isDoctor) {
                        setDoctorViewSpecialityId(e.target.value || null);
                      } else {
                        setSelectedSpecialityId(e.target.value);
                      }
                      setForm((f) => ({ ...f, templateId: "" }));
                    }}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {(!isDoctor || !doctorViewSpecialityId) && (
                      <option value="">
                        {t("consultation.selectSpeciality")}
                      </option>
                    )}
                    {specialities.map((s) => (
                      <option key={s.id} value={s.id}>
                        {getLocalizedName(s, i18n.language)}{isDoctor && s.id === doctorSpecialityId ? ` (${t("consultation.mySpeciality")})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                )}

                {showStudyPicker &&
                  (selectedSpecialityId || (isDoctor && !!doctorViewSpecialityId)) && (
                  <div className="space-y-2">
                    <Label>{t("consultation.template")}</Label>
                    {templates.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {t("consultation.noTemplates")}
                      </p>
                    ) : isDoctor && doctorSpecialityId && doctorViewSpecialityId === doctorSpecialityId ? (() => {
                      const myTemplates = templates.filter((tpl) => tpl.specialityId === doctorSpecialityId);
                      const otherTemplates = templates.filter((tpl) => tpl.specialityId !== doctorSpecialityId);
                      const renderTemplateButton = (tpl: typeof templates[number]) => (
                        <button
                          key={tpl.id}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, templateId: tpl.id }))}
                          className={`p-2 text-sm rounded-md border transition-colors text-left ${
                            form.templateId === tpl.id
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-input hover:bg-accent"
                          }`}
                        >
                          <span>{getLocalizedName(tpl, i18n.language)}</span>
                          {tpl.isGeneral && (
                            <span
                              className={`ml-2 text-xs px-1.5 py-0.5 rounded font-medium ${form.templateId === tpl.id ? "bg-primary-foreground/20 text-primary-foreground" : "bg-primary/10 text-primary"}`}
                            >
                              General
                            </span>
                          )}
                        </button>
                      );
                      return (
                        <div className="space-y-3">
                          {myTemplates.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                {t("consultation.mySpecialityTemplates")}
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                {myTemplates.map(renderTemplateButton)}
                              </div>
                            </div>
                          )}
                          {otherTemplates.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                {t("consultation.otherTemplates")}
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                {otherTemplates.map(renderTemplateButton)}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })() : (
                      <div className="grid grid-cols-2 gap-2">
                        {templates.map((tpl) => (
                          <button
                            key={tpl.id}
                            type="button"
                            onClick={() =>
                              setForm((f) => ({ ...f, templateId: tpl.id }))
                            }
                            className={`p-2 text-sm rounded-md border transition-colors text-left ${
                              form.templateId === tpl.id
                                ? "bg-primary text-primary-foreground border-primary"
                                : "border-input hover:bg-accent"
                            }`}
                          >
                            <span>{getLocalizedName(tpl, i18n.language)}</span>
                            {tpl.isGeneral && (
                              <span
                                className={`ml-2 text-xs px-1.5 py-0.5 rounded font-medium ${form.templateId === tpl.id ? "bg-primary-foreground/20 text-primary-foreground" : "bg-primary/10 text-primary"}`}
                              >
                                General
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {error && <p className="text-sm text-destructive">{error}</p>}
              </form>
            </CardContent>
            <CardFooter>
              <Button
                type="submit"
                form="new-consultation-form"
                className="w-full"
                disabled={createMutation.isPending || !canStart}
              >
                {createMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {t("consultation.startButton")}
              </Button>
            </CardFooter>
          </Card>

          {selectedPatient && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("consultation.pastConsultations")}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {selectedPatient.name}
                </p>
              </CardHeader>
              <CardContent>
                {historyLoading ? (
                  <Spinner className="py-4" />
                ) : patientHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    {t("consultation.noPastConsultations")}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {patientHistory.map((c) => (
                      <li key={c.id}>
                        <a
                          href={`/consultation/${c.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="block rounded-md border px-3 py-2 text-sm cursor-pointer hover:shadow-md transition-shadow"
                        >
                          <p className="font-medium">
                            {formatDate(c.startedAt)}
                          </p>
                          {c.summary && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {c.summary}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {statusLabel(c)}
                          </p>
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>

              {historyTotalPages > 1 && (
                <CardFooter className="flex items-center justify-between pt-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={historyPage <= 1}
                    onClick={() => setHistoryPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {historyPage} / {historyTotalPages}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={historyPage >= historyTotalPages}
                    onClick={() => setHistoryPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </CardFooter>
              )}
            </Card>
          )}
        </div>
      </div>

      <Dialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("consultation.consentTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("consultation.consentDescription")}
          </p>
          <div className="flex flex-col gap-2 pt-2">
            <Button
              className="w-full"
              onClick={handleConsentConfirm}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t("consultation.consentConfirm")}
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowConsentDialog(false)}
              disabled={createMutation.isPending}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showAddPatient}
        onOpenChange={(open) => {
          setShowAddPatient(open);
          if (!open) {
            setAddPatientForm(EMPTY_PATIENT_FORM);
            setAddPatientError("");
            setDuplicateConflict(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("patients.addPatient")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPatientSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label>{t("patients.fullName")} *</Label>
              <Input
                value={addPatientForm.name}
                onChange={(e) =>
                  setAddPatientForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder={t("patients.fullName")}
                required
              />
            </div>
            <div className="space-y-1">
              <Label>{t("patients.personalNumber")} *</Label>
              <Input
                value={addPatientForm.personalNumber}
                onChange={(e) =>
                  setAddPatientForm((f) => ({
                    ...f,
                    personalNumber: e.target.value,
                  }))
                }
                placeholder={t("patients.personalNumber")}
                required
              />
            </div>
            <div className="space-y-1">
              <Label>{t("patients.gender")}</Label>
              <select
                value={addPatientForm.gender}
                onChange={(e) =>
                  setAddPatientForm((f) => ({
                    ...f,
                    gender: e.target.value as Gender,
                  }))
                }
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                required
              >
                <option value={Gender.Male}>{t("patients.male")}</option>
                <option value={Gender.Female}>{t("patients.female")}</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t("patients.phone")}</Label>
              <Input
                value={addPatientForm.phone}
                onChange={(e) =>
                  setAddPatientForm((f) => ({ ...f, phone: e.target.value }))
                }
                placeholder={t("patients.phone")}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("patients.dateOfBirth")}</Label>
              <Input
                type="date"
                value={addPatientForm.dateOfBirth}
                onChange={(e) =>
                  setAddPatientForm((f) => ({
                    ...f,
                    dateOfBirth: e.target.value,
                  }))
                }
              />
            </div>
            {addPatientError && (
              <p className="text-sm text-destructive">{addPatientError}</p>
            )}
            {duplicateConflict && (
              <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 flex flex-col gap-2">
                <div className="flex items-start gap-2 text-yellow-800 text-sm">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    {t("patients.duplicateIIN", {
                      iin: duplicateConflict.personalNumber,
                      name: duplicateConflict.name,
                    })}
                  </span>
                </div>
                <div className="flex gap-2 pl-6">
                  {isDoctor && profile && (
                    <Button
                      type="button"
                      size="sm"
                      disabled={sharePatientMutation.isPending}
                      onClick={() => {
                        sharePatientMutation.mutate(
                          { patientId: duplicateConflict.id, doctorId: profile.id },
                          {
                            onSuccess: () => {
                              handleSelectPatient({ ...duplicateConflict } as PatientDTO);
                              setShowAddPatient(false);
                              setAddPatientForm(EMPTY_PATIENT_FORM);
                              setDuplicateConflict(null);
                            },
                          },
                        );
                      }}
                    >
                      {sharePatientMutation.isPending && (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      )}
                      {t("patients.attachPatient")}
                    </Button>
                  )}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowAddPatient(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={createPatientMutation.isPending}
              >
                {createPatientMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {t("common.save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
